import { type FC, useEffect } from "react";

import { useSelector } from "@jti/store/reduxHooks";

import type { CheckOutSteps } from "@checkout/constants/checkout-constants";
import { useGtm } from "@checkout/hooks/gtm/useGtm";
import { useFreeTrial } from "@checkout/hooks/useFreeTrial";
import { useOrder } from "@checkout/hooks/useOrder";

type CheckoutProgressGtmObserverProps = {
  step?: CheckOutSteps;
};

export const CheckoutProgressGtmObserver: FC<CheckoutProgressGtmObserverProps> = ({ step }) => {
  const { order, orderFetchStatus } = useOrder();
  const { dispatchGtmCheckoutProgress, dispatchGtmPageViewEvent } = useGtm();
  const { isFreeTrial } = useFreeTrial();

  const defaultCart = useSelector((state) => state.cart);
  const freeTrialCart = useSelector((state) => state.freeTrialCart);

  const areItemsLoading = isFreeTrial ? freeTrialCart.areItemsLoading : defaultCart.itemsPending;
  const cartItemsCount = isFreeTrial ? (freeTrialCart.items?.length ?? 0) : (defaultCart.items?.length ?? 0);

  useEffect(() => {
    dispatchGtmPageViewEvent();
  }, []);

  useEffect(() => {
    if (!step) {
      return;
    }

    const cart = isFreeTrial ? freeTrialCart : defaultCart;
    const trackingData = order || cart;

    const isTrackingDataEmpty = (trackingData?.items ?? []).length === 0;
    const shouldSetTracking =
      !defaultCart.notLoggedIn && !areItemsLoading && orderFetchStatus !== "pending" && !isTrackingDataEmpty;

    if (shouldSetTracking) {
      dispatchGtmCheckoutProgress(trackingData, step);
    }
  }, [step, areItemsLoading, orderFetchStatus, cartItemsCount]);

  return null;
};
